<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue';
import { KeyRound, Plus, UserCheck, UserX, Users, X } from '@lucide/vue';
import { useRouter } from 'vue-router';
import AppShell from '../components/AppShell.vue';
import { api } from '../api/client';

const router = useRouter();
const me = ref<{ id: string; email: string; name: string; role: 'admin' | 'editor' }>();
const users = ref<any[]>([]);
const loading = ref(true);
const error = ref('');

const creating = ref(false);
const createForm = reactive({ name: '', email: '', password: '', repeat: '', role: 'editor' as 'admin' | 'editor' });
const createError = ref('');
const saving = ref(false);

const passwordUserId = ref('');
const passwordForm = reactive({ password: '', repeat: '' });
const rowError = ref('');
const rowNotice = ref<{ id: string; text: string }>();

const ownForm = reactive({ currentPassword: '', newPassword: '', repeat: '' });
const ownError = ref('');
const ownNotice = ref('');

function handleAuth(e: any) {
  if (e.status === 401) { router.push('/login'); return true; }
  return false;
}

async function load() {
  try {
    me.value = await api.me();
    // Редактору доступна только смена собственного пароля.
    if (me.value.role === 'admin') users.value = await api.users();
  } catch (e: any) {
    if (!handleAuth(e)) error.value = e.message;
  } finally {
    loading.value = false;
  }
}

function formatDate(value: string | null) {
  return value ? new Date(value).toLocaleString('ru-RU') : '—';
}

function openCreate() {
  Object.assign(createForm, { name: '', email: '', password: '', repeat: '', role: 'editor' });
  createError.value = '';
  creating.value = true;
}

async function createUser() {
  createError.value = '';
  if (createForm.password !== createForm.repeat) { createError.value = 'Пароли не совпадают'; return; }
  saving.value = true;
  try {
    const user = await api.createUser({ name: createForm.name, email: createForm.email, password: createForm.password, role: createForm.role });
    users.value = [...users.value, user];
    creating.value = false;
    rowNotice.value = { id: user.id, text: 'Пользователь создан' };
  } catch (e: any) {
    if (!handleAuth(e)) createError.value = e.message;
  } finally {
    saving.value = false;
  }
}

function startPassword(id: string) {
  passwordUserId.value = id;
  Object.assign(passwordForm, { password: '', repeat: '' });
  rowError.value = '';
  rowNotice.value = undefined;
}

async function setUserPassword(user: any) {
  rowError.value = '';
  if (passwordForm.password !== passwordForm.repeat) { rowError.value = 'Пароли не совпадают'; return; }
  saving.value = true;
  try {
    await api.setUserPassword(user.id, passwordForm.password);
    passwordUserId.value = '';
    rowNotice.value = { id: user.id, text: 'Пароль изменён, сессии пользователя завершены' };
  } catch (e: any) {
    if (!handleAuth(e)) rowError.value = e.message;
  } finally {
    saving.value = false;
  }
}

async function toggleActive(user: any) {
  rowError.value = '';
  rowNotice.value = undefined;
  if (user.isActive && !confirm(`Отключить пользователя ${user.email}? Он не сможет войти в админку.`)) return;
  try {
    const updated = await api.updateUser(user.id, { isActive: !user.isActive });
    Object.assign(user, updated);
  } catch (e: any) {
    if (!handleAuth(e)) rowNotice.value = { id: user.id, text: e.message };
  }
}

async function changeRole(user: any, role: 'admin' | 'editor') {
  rowNotice.value = undefined;
  try {
    Object.assign(user, await api.updateUser(user.id, { role }));
    rowNotice.value = { id: user.id, text: role === 'admin' ? 'Теперь администратор: видит все формы' : 'Теперь редактор: видит только свои и открытые ему формы' };
  } catch (e: any) {
    if (!handleAuth(e)) { rowNotice.value = { id: user.id, text: e.message }; users.value = await api.users(); }
  }
}

async function changeOwnPassword() {
  ownError.value = '';
  ownNotice.value = '';
  if (ownForm.newPassword !== ownForm.repeat) { ownError.value = 'Новые пароли не совпадают'; return; }
  saving.value = true;
  try {
    await api.changeMyPassword(ownForm.currentPassword, ownForm.newPassword);
    Object.assign(ownForm, { currentPassword: '', newPassword: '', repeat: '' });
    ownNotice.value = 'Пароль изменён. Другие ваши сессии завершены.';
  } catch (e: any) {
    if (!handleAuth(e)) ownError.value = e.message;
  } finally {
    saving.value = false;
  }
}

onMounted(load);
</script>

<template>
  <AppShell>
    <div class="page-heading">
      <div><h1>{{ me?.role === 'editor' ? 'Мой пароль' : 'Пользователи' }}</h1><p>{{ me?.role === 'editor' ? 'Смена пароля для входа в админку' : 'Администратор видит все формы; редактор — только свои и открытые ему' }}</p></div>
      <button v-if="!creating && me?.role === 'admin'" class="btn btn-primary" @click="openCreate"><Plus :size="17"/>Добавить</button>
    </div>

    <form v-if="creating" class="users-panel" autocomplete="off" @submit.prevent="createUser">
      <header><h2>Новый пользователь</h2><button type="button" class="icon-btn" title="Закрыть" @click="creating = false"><X :size="18"/></button></header>
      <div class="users-form-grid">
        <label class="input-label">Имя<input v-model="createForm.name" class="control" maxlength="160" placeholder="Необязательно"/></label>
        <label class="input-label">Email<input v-model="createForm.email" class="control" type="email" required autocomplete="off"/></label>
        <label class="input-label">Пароль<input v-model="createForm.password" class="control" type="password" minlength="8" required autocomplete="new-password"/></label>
        <label class="input-label">Повторите пароль<input v-model="createForm.repeat" class="control" type="password" minlength="8" required autocomplete="new-password"/></label>
        <label class="input-label">Роль<select v-model="createForm.role" class="control"><option value="editor">Редактор — только свои и открытые ему формы</option><option value="admin">Администратор — все формы и пользователи</option></select></label>
      </div>
      <p class="users-hint">Не короче 8 символов. Передайте пароль пользователю безопасным способом — после входа он сможет сменить его сам.</p>
      <p v-if="createError" class="alert error">{{ createError }}</p>
      <div><button class="btn btn-primary" :disabled="saving">Создать пользователя</button></div>
    </form>

    <template v-if="me?.role !== 'editor'">
    <div v-if="loading" class="journal-table skeleton"></div>
    <p v-else-if="error" class="alert error">{{ error }}</p>
    <section v-else-if="!users.length" class="empty-state journal-empty"><span class="empty-icon"><Users :size="30"/></span><h2>Пользователей нет</h2></section>
    <div v-else class="journal-table">
      <div class="users-row journal-header"><span>Пользователь</span><span>Роль и статус</span><span>Последний вход</span><span>Действия</span></div>
      <article v-for="user in users" :key="user.id" class="users-row">
        <div class="users-identity"><strong>{{ user.name || user.email }}<span v-if="user.id === me?.id" class="users-me">Вы</span></strong><small v-if="user.name">{{ user.email }}</small><small>Пароль изменён {{ formatDate(user.passwordChangedAt) }}</small></div>
        <div class="users-role"><select v-if="user.id !== me?.id" class="control role-select" :value="user.role" aria-label="Роль" @change="changeRole(user, ($event.target as HTMLSelectElement).value as 'admin' | 'editor')"><option value="editor">Редактор</option><option value="admin">Администратор</option></select><span v-else class="role-badge">Администратор</span><span :class="['consent-state', user.isActive ? 'accepted' : 'revoked']">{{ user.isActive ? 'Активен' : 'Отключён' }}</span></div>
        <time>{{ formatDate(user.lastLoginAt) }}</time>
        <div class="users-actions">
          <template v-if="user.id === me?.id"><a class="users-link" href="#my-password">Сменить свой пароль</a></template>
          <template v-else>
            <button v-if="passwordUserId !== user.id" class="users-link" @click="startPassword(user.id)"><KeyRound :size="14"/>Задать пароль</button>
            <button class="users-link" :class="{ danger: user.isActive }" @click="toggleActive(user)"><component :is="user.isActive ? UserX : UserCheck" :size="14"/>{{ user.isActive ? 'Отключить' : 'Включить' }}</button>
          </template>
          <form v-if="passwordUserId === user.id" class="users-inline" @submit.prevent="setUserPassword(user)">
            <input v-model="passwordForm.password" class="control" type="password" minlength="8" required placeholder="Новый пароль" autocomplete="new-password"/>
            <input v-model="passwordForm.repeat" class="control" type="password" minlength="8" required placeholder="Повторите пароль" autocomplete="new-password"/>
            <p v-if="rowError" class="field-error">{{ rowError }}</p>
            <div><button class="btn btn-primary" :disabled="saving">Сохранить</button><button type="button" class="icon-btn" title="Отмена" @click="passwordUserId = ''"><X :size="17"/></button></div>
          </form>
          <small v-if="rowNotice?.id === user.id" class="users-notice">{{ rowNotice?.text }}</small>
        </div>
      </article>
    </div>

    </template>

    <form id="my-password" class="users-panel" @submit.prevent="changeOwnPassword">
      <header><h2>Мой пароль</h2></header>
      <div class="users-form-grid three">
        <label class="input-label">Текущий пароль<input v-model="ownForm.currentPassword" class="control" type="password" required autocomplete="current-password"/></label>
        <label class="input-label">Новый пароль<input v-model="ownForm.newPassword" class="control" type="password" minlength="8" required autocomplete="new-password"/></label>
        <label class="input-label">Повторите новый<input v-model="ownForm.repeat" class="control" type="password" minlength="8" required autocomplete="new-password"/></label>
      </div>
      <p v-if="ownError" class="alert error">{{ ownError }}</p>
      <p v-if="ownNotice" class="alert success">{{ ownNotice }}</p>
      <div><button class="btn btn-primary" :disabled="saving"><KeyRound :size="16"/>Сменить пароль</button></div>
    </form>
  </AppShell>
</template>
